import { AccessIcon, EmptyState, LazyLoader } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TimetableVersionLessonProvider } from '../../../../components/Lessons/TimetableVersion/TimetableVersionLessonProvider';
import { Page } from '../../../../components/Page/Page';
import { useTimeGridEntries } from '../../../../components/TimeGrid/graphql/hooks/useTimeGridEntries';
import { useTimetableForVersion } from '../../../../components/Timetable/hooks/useTimetables';
import { TimetableProvider } from '../../../../components/TimetableGrid/TimetableProvider';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { AvailabilityStore } from '../../../../stores/AvailabilityStore';
import { StoreHandler } from '../../../../stores/StoreHandler';
import { TimeAvailabilityStore } from '../../../../stores/TimetableVersion/TimeAvailabilityStore';
import {
  ClassGridCard,
  useDuplicateTimetableVersionMutation,
  useSetTimetableVersionEditableMutation,
} from '../../../../types/planung-graphql-client-defs';
import { createEventsForVersion } from '../../../../utils/eventsApi';
import { BlockPhase, TimetableVersionLayout } from './TimetableVersionLayout';

export const timeAvailabilityStoreHandler = new StoreHandler<TimeAvailabilityStore>();
export const availabilityStore = new AvailabilityStore();
export type ClassGridCards = ClassGridCard;

export const TimetableVersion = observer(({ baseSlug }: { baseSlug: string }) => {
  const { t } = useTranslation();
  const { versionUuid } = useParams();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();
  const permissions = usePermissionChecker();

  const { timetable } = useTimetableForVersion(versionUuid);

  const timegridUuid = timetable?.timegridConfigs[0]?.timegridUuid ?? undefined;

  const timeGridEntries = useTimeGridEntries(timegridUuid);

  const schoolYear = useUserConfigContext();

  const context = useMemorizedCacheTag('TIMETABLE_VERSIONS');
  const duplicateVersionContext = useMemo(() => {
    return {
      additionalTypenames: ['TimetableVersion', 'TimetableCard', 'LessonClass'],
    };
  }, []);

  const [, setVersionEditable] = useSetTimetableVersionEditableMutation();
  const [, duplicateVersion] = useDuplicateTimetableVersionMutation();

  const [ready, setReady] = useState(false);
  const [inSchoolYear, setInSchoolYear] = useState<boolean>(false);

  useEffect(() => {
    if (versionUuid) {
      const inSchoolYear = timetable?.schoolYearUuid === schoolYear.selectedSchoolYear?.uuid;
      setInSchoolYear(inSchoolYear);
    }
  }, [versionUuid, timetable, schoolYear.selectedSchoolYear]);

  useEffect(() => {
    if (versionUuid && timetable && timegridUuid && timeGridEntries) {
      timeAvailabilityStoreHandler.register(versionUuid, () => {
        return new TimeAvailabilityStore('00:00', '24:00', undefined, timeGridEntries);
      });
      setReady(true);
    }
  }, [timetable, versionUuid, timegridUuid, timeGridEntries]);

  const blockPhase = useMemo((): BlockPhase => {
    const blocks = timetable?.timetableBlocks ?? [];
    if (blocks.length === 0) return null;
    const today = dayjs();
    if (blocks.some((b) => dayjs(b.start).isSameOrBefore(today, 'day') && dayjs(b.end).isSameOrAfter(today, 'day'))) {
      return 'running';
    }
    if (blocks.some((b) => dayjs(b.start).isAfter(today, 'day'))) {
      return 'future';
    }
    return 'past';
  }, [timetable]);

  async function handleStartFix() {
    if (!versionUuid) return;
    const updateResult = await setVersionEditable({ uuid: versionUuid, editable: true }, context);
    if (updateResult.error) throw updateResult.error;
    const duplicateResult = await duplicateVersion(
      {
        timetableVersionUuid: versionUuid,
        organizationUuid,
        options: { snapshot: true },
      },
      duplicateVersionContext,
    );
    if (duplicateResult.error) throw duplicateResult.error;
  }

  // Gibt `true` zurück, wenn die Aktion tatsächlich durchlief – nur dann darf die
  // TopBar den temporären Edit-Status zurücksetzen.
  async function handleCompleteFix(): Promise<boolean> {
    if (!versionUuid) return false;
    // Ein Entwurf bleibt bearbeitbar („Termine erstellen“ erstellt nur die Termine); gesperrt wird eine Version erst,
    // wenn ihr Stundenplan beginnt (Backend: applyDraftStateFromBlocks).
    if (!timetable?.draft) {
      const updateResult = await setVersionEditable({ uuid: versionUuid, editable: false }, context);
      if (updateResult.error) return false;
    }
    if (blockPhase !== 'past') {
      await createEventsForVersion({ organizationUuid, timetableVersionUuid: versionUuid });
    }
    return true;
  }

  // Korrigieren nur mit Schreibrecht; ohne Handler blendet die TopBar die Knöpfe aus
  const canFix = !!permissions?.canWriteTimetable();

  return (
    <Page isForbidden={!permissions?.canReadTimetable()} fullWidth fullHeight>
      {versionUuid && ready && (
        <>
          {inSchoolYear ? (
            <Suspense fallback={<LazyLoader embedded transparent size='xxl' forceHeight='60vh' />}>
              <TimetableVersionLessonProvider>
                <TimetableProvider versionUuid={versionUuid}>
                  <TimetableVersionLayout
                    versionUuid={versionUuid}
                    baseSlug={baseSlug}
                    blockPhase={blockPhase}
                    onStartFix={canFix ? handleStartFix : undefined}
                    onCompleteFix={canFix ? handleCompleteFix : undefined}
                  />
                </TimetableProvider>
              </TimetableVersionLessonProvider>
            </Suspense>
          ) : (
            <EmptyState
              icon={<AccessIcon color='var(--color-error)' />}
              title={t('timetableVersion.notInSchoolYear')}
              forcedHeight='60vh'
            />
          )}
        </>
      )}
    </Page>
  );
});
